import React from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SectionInputsModal2 from "./SectionInputsModal2";
import { API_get_learning_section_detail } from "../../../constants";
import { getRightworkAPI } from "../requests/utilities/requests";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { TrainingEmptyState } from "./TrainingEmptyState";
import LearningContentPreviewModal from "./LearningContentPreviewModal";
import LearningPreviewMenuButton from "./LearningPreviewMenuButton";
import { formatDate } from "../requests/utilities/helpers";
import LibraryFilterChips from "./LibraryFilterChips";

const ASSIGNABLE_OPTIONS = [
  { value: "assignable", label: "Assignable" },
  { value: "not_assignable", label: "Not assignable" },
  { value: "unassigned", label: "Not in modules" },
];

const filterSelectSx = {
  height: 42,
  minWidth: 170,
  borderRadius: "8px",
  backgroundColor: "white",
  fontSize: 14,
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a78bfa" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "12px",
  },
};

function SectionsDisplay(props) {
  const [open, setOpen] = React.useState(false);
  const [modalEdit, setModalEdit] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [previewSection, setPreviewSection] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [assignableFilter, setAssignableFilter] = React.useState("all");
  const [sortKey, setSortKey] = React.useState("recent");

  const sections = React.useMemo(
    () => props.sectionsData || [],
    [props.sectionsData]
  );

  const categories = React.useMemo(() => {
    return [...new Set(sections.map((section) => section.category).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [sections]);

  const fetchSectionDetail = async (showLoading, learning_section_uuid) => {
    setIsLoading(showLoading);
    const api_params = {
      company_id: props.homeCompany,
      learning_section_uuid: learning_section_uuid,
    };
    try {
      const res = await getRightworkAPI(API_get_learning_section_detail, api_params);

      let mapSignedUrlToFileUuid =
        localStorage.getItem("mapSignedUrlToFileUuid") || "{}";
      mapSignedUrlToFileUuid = JSON.parse(mapSignedUrlToFileUuid);
      const rawBlocksAsJson =
        res.data.redux?.payload?.learning_section.as_json || [];
      rawBlocksAsJson.forEach((block) => {
        const validTypes = ["image", "video", "audio", "file", "pdf"];
        if (!validTypes.includes(block.type)) return;
        if (!block.props?.url || !block.props?.file_uuid) return;
        mapSignedUrlToFileUuid[block.props.url] = block.props.file_uuid;
      });
      localStorage.setItem(
        "mapSignedUrlToFileUuid",
        JSON.stringify(mapSignedUrlToFileUuid)
      );

      if (res.status === 200) {
        props.setModalDataSection(res.data.redux?.payload?.learning_section);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      throw Error(error);
    }
  };

  const filteredSections = React.useMemo(() => {
    const searchParts = searchInput.toLowerCase().split(" ").filter(Boolean);
    const filtered = sections.filter((section) => {
      const haystack = [
        section.name,
        section.category,
        ...(section.modules || []).map((module) => module.name),
        ...(section.tracks || []).map((track) => track.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      if (!searchParts.every((part) => haystack.includes(part))) return false;
      if (categoryFilter !== "all" && (section.category || "") !== categoryFilter) {
        return false;
      }
      if (assignableFilter === "assignable" && !section.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "not_assignable" && section.is_directly_assignable) {
        return false;
      }
      if (assignableFilter === "unassigned" && (section.modules || []).length > 0) {
        return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (sortKey === "name") {
        return (a.name || "").localeCompare(b.name || "");
      }
      if (sortKey === "modules") {
        return (b.modules?.length || 0) - (a.modules?.length || 0);
      }
      if (sortKey === "tracks") {
        return (b.tracks?.length || 0) - (a.tracks?.length || 0);
      }
      return (b.updated_at || b.created_at || 0) - (a.updated_at || a.created_at || 0);
    });
  }, [sections, searchInput, categoryFilter, assignableFilter, sortKey]);

  const activeFilterCount =
    (categoryFilter !== "all" ? 1 : 0) +
    (assignableFilter !== "all" ? 1 : 0);

  const handleEditSection = (section) => {
    setModalEdit(true);
    fetchSectionDetail(true, section.uuid);
    setOpen(true);
  };

  return (
    <>
      <div className="min-h-screen bg-white">
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 py-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-slate-900 mb-1">
                  Learning Sections
                </h1>
                <p className="text-sm text-slate-600">
                  Employee-facing pages with text, media, quizzes, forms, and embeds
                </p>
              </div>
              <Tooltip title="Create new section">
                <button
                  className="bg-violet-700 hover:bg-violet-800 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-sm"
                  onClick={() => {
                    setModalEdit(false);
                    props.setModalDataSection(props.default_data_section);
                    setOpen(true);
                  }}
                >
                  <AddIcon style={{ fontSize: "20px" }} />
                  <span className="font-medium">New Section</span>
                </button>
              </Tooltip>
            </div>

            <div className="flex items-center gap-6 text-sm text-slate-600 mb-6">
              <div className="flex items-center gap-2">
                <ViewHeadlineIcon style={{ fontSize: "18px" }} />
                <span>
                  {filteredSections.length}{" "}
                  {filteredSections.length === 1 ? "Section" : "Sections"}
                </span>
              </div>
              {searchInput && (
                <div className="flex items-center gap-2 text-violet-600">
                  <SearchIcon style={{ fontSize: "16px" }} />
                  <span>Showing search results</span>
                </div>
              )}
              {activeFilterCount > 0 && (
                <div className="flex items-center gap-2 text-violet-600">
                  <FilterListIcon style={{ fontSize: "16px" }} />
                  <span>
                  {activeFilterCount}{" "}
                  {activeFilterCount === 1 ? "filter" : "filters"} active
                </span>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full max-w-md">
                <SearchIcon
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  style={{ fontSize: "20px" }}
                />
                <input
                  className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100 text-sm"
                  type="text"
                  placeholder="Search sections..."
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                />
              </div>

              <Select
                size="small"
                displayEmpty
                sx={filterSelectSx}
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value)}
              >
                <MenuItem value="all" sx={{ fontSize: 14 }}>
                  All categories
                </MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category} sx={{ fontSize: 14 }}>
                    {category}
                  </MenuItem>
                ))}
              </Select>

              <Select
                size="small"
                sx={filterSelectSx}
                value={sortKey}
                onChange={(event) => setSortKey(event.target.value)}
              >
                <MenuItem value="recent" sx={{ fontSize: 14 }}>
                  Recently updated
                </MenuItem>
                <MenuItem value="name" sx={{ fontSize: 14 }}>Name</MenuItem>
                <MenuItem value="modules" sx={{ fontSize: 14 }}>
                  Most modules
                </MenuItem>
                <MenuItem value="tracks" sx={{ fontSize: 14 }}>
                  Most tracks
                </MenuItem>
              </Select>
            </div>

            <div className="mt-4">
              <LibraryFilterChips
                groups={[
                  {
                    key: "assignable",
                    value: assignableFilter,
                    onChange: setAssignableFilter,
                    options: ASSIGNABLE_OPTIONS,
                  },
                ]}
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-8">
          {filteredSections.length === 0 ? (
            <TrainingEmptyState
              icon={ViewHeadlineIcon}
              plural="sections"
              blurb="Create the pages where employees read, watch, practice, and complete training steps."
              createLabel="Create your first section"
              searching={Boolean(searchInput || activeFilterCount)}
              onCreate={() => {
                setModalEdit(false);
                props.setModalDataSection(props.default_data_section);
                setOpen(true);
              }}
            />
          ) : (
            <div className="grid grid-cols-2 xl:grid-cols-1 gap-3">
              {filteredSections.map((section) => (
                <SectionCard
                  key={section.uuid}
                  section={section}
                  onEdit={() => handleEditSection(section)}
                  onPreview={(device) => setPreviewSection({ item: section, device })}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <SectionInputsModal2
        open={open}
        handleClose={() => setOpen(false)}
        modalEdit={modalEdit}
        isLoading={isLoading}
        modalDataSection={props.modalDataSection}
        setModalDataSection={props.setModalDataSection}
        post_learning_section={props.post_learning_section}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        delete_learning_section={props.delete_learning_section}
        modalDataQuiz={props.modalDataQuiz}
        setModalDataQuiz={props.setModalDataQuiz}
        default_data_quiz={props.default_data_quiz}
        post_learning_quiz={props.post_learning_quiz}
        quizzesData={props.quizzesData}
        sectionsData={props.sectionsData}
        modulesData={props.modulesData}
      />

      <LearningContentPreviewModal
        open={Boolean(previewSection)}
        onClose={() => setPreviewSection(null)}
        previewType="section"
        item={previewSection?.item}
        initialDevice={previewSection?.device}
        homeCompany={props.homeCompany}
        quizzesData={props.quizzesData}
      />
    </>
  );
}

function SectionCard({ section, onEdit, onPreview }) {
  const moduleCount = section.modules?.length || 0;
  const trackCount = section.tracks?.length || 0;
  const primaryModuleColor = section.modules?.[0]?.color || "bg-slate-700";
  const updatedLabel = section.updated_at
    ? `Updated ${formatDate(section.updated_at)}`
    : "";
  const metadataLabel =
    `${moduleCount} ${moduleCount === 1 ? "module" : "modules"}` +
    ` · ${trackCount} ${trackCount === 1 ? "track" : "tracks"}`;

  return (
    <div
      className="group relative cursor-pointer rounded-lg border border-slate-200 bg-white p-4 transition-all duration-200 hover:border-slate-300 hover:shadow-md"
      onClick={onEdit}
    >
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${primaryModuleColor}`}>
          <ViewHeadlineIcon className="text-white" style={{ fontSize: 20 }} />
        </div>

        <div className="min-w-0 flex-1 pr-20">
          <div className="min-w-0">
            <h3 className="line-clamp-2 text-sm font-semibold text-slate-900">
              {section.name || "Untitled section"}
            </h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 pr-32 text-xs text-slate-500">
              <span>{metadataLabel}</span>
              {section.is_directly_assignable && (
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700 ring-1 ring-emerald-100">
                  Assignable
                </span>
              )}
              {section.category && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                  {section.category}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute right-3 top-3 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <LearningPreviewMenuButton
          onSelect={onPreview}
          iconButtonSx={{ color: "#475569" }}
        />
        <Tooltip title="Edit section">
          <span>
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
              sx={{ color: "#475569" }}
            >
              <EditIcon style={{ fontSize: 18 }} />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      {updatedLabel && (
        <span className="absolute bottom-4 right-4 text-xs text-slate-400">
          {updatedLabel}
        </span>
      )}
    </div>
  );
}

export default SectionsDisplay;
